import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConvexReactClient } from 'convex/react'
import { ConvexProviderWithAuthKit } from '@convex-dev/workos'
import { AuthKitProvider, useAuth } from '@workos-inc/authkit-react'
import { ErrorBoundary } from '@sentry/react'
import { routeTree } from './routeTree.gen'
import { env } from '@/lib/env'
import { initSentry } from '@/lib/sentry'
import '@/lib/i18n'
import { Button } from '@/components/ui/button'

const queryClient = new QueryClient()

// Initialize Sentry as early as possible
initSentry()

const convex = new ConvexReactClient(env.VITE_CONVEX_URL)

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('app')!

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <ErrorBoundary
      fallback={({ error }) => (
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="text-center">
            <h1 className="mb-4 text-2xl font-bold">Something went wrong</h1>
            <p className="mb-4 text-muted-foreground">
              An error occurred. Please refresh the page.
            </p>
            <Button onClick={() => window.location.reload()}>Refresh Page</Button>
            {import.meta.env.DEV && (
              <pre className="mt-4 overflow-auto rounded-md bg-muted p-4 text-left text-sm">
                {error?.toString()}
              </pre>
            )}
          </div>
        </div>
      )}
      showDialog
    >
      <AuthKitProvider
        clientId={env.VITE_WORKOS_CLIENT_ID}
        redirectUri={env.VITE_WORKOS_REDIRECT_URI}
        devMode={true}
        onRefreshFailure={(error) => {
          // devMode=true stores the refresh token in localStorage and sends it in the request body,
          // bypassing third-party cookie blocking (Firefox ETP, Safari ITP, Chrome Tracking Protection).
          // Without this, the library sends credentials: include to api.workos.com but browsers
          // block that third-party cookie, so WorkOS receives a refresh request with no token → 400.
          console.error('[Auth] WorkOS refresh token failed — session likely expired', {
            error,
            at: new Date().toISOString(),
            url: window.location.href,
          })
        }}
      >
        <ConvexProviderWithAuthKit client={convex} useAuth={useAuth}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </ConvexProviderWithAuthKit>
      </AuthKitProvider>
    </ErrorBoundary>,
  )
}
